import React, { useState } from 'react';
import { FaRobot, FaPaperPlane, FaTimes, FaMinus } from 'react-icons/fa';
import { API_BASE_URL } from '../services/api';

const initialFields = [
  { key: 'businessName', label: 'What is your business name?', type: 'text', required: true },
  { key: 'ownerName', label: 'Owner name?', type: 'text', required: true },
  { key: 'email', label: 'Business email?', type: 'email', required: true, validate: v => /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(v) },
  { key: 'phone', label: 'Business phone?', type: 'tel', required: true, validate: v => /^\+?[\d\s-]{10,}$/.test(v) },
  { key: 'address', label: 'Business address?', type: 'text', required: true },
  { key: 'city', label: 'Business city?', type: 'text', required: true },
  { key: 'category', label: 'Business category?', type: 'text', required: true },
  { key: 'description', label: 'Short business description?', type: 'textarea', required: true },
  { key: 'website', label: 'Business website (optional)?', type: 'url', required: false },
  { key: 'profilePhoto', label: 'Upload a business profile photo (optional)', type: 'file', required: false },
];

function BusinessListingBot() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [input, setInput] = useState('');
  const [fileInput, setFileInput] = useState(null);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [dbStatus, setDbStatus] = useState('');
  const [closed, setClosed] = useState(false);
  const [minimized, setMinimized] = useState(true); // Start minimized by default

  const field = initialFields[step];

  // For chat bubbles
  const chatHistory = [];
  for (let i = 0; i < step; i++) {
    chatHistory.push({ from: 'bot', text: initialFields[i].label });
    chatHistory.push({ from: 'user', text: answers[initialFields[i].key] });
  }
  if (!submitted) {
    chatHistory.push({ from: 'bot', text: field.label });
  }

  const handleSend = () => {
    if (field.type === 'file') {
      if (fileInput) {
        setAnswers({ ...answers, [field.key]: fileInput });
        setFileInput(null);
        setError('');
        if (step < initialFields.length - 1) {
          setStep(step + 1);
        } else {
          setSubmitted(true);
        }
      } else {
        if (field.required) setError('Please select a file.');
        else {
          setAnswers({ ...answers, [field.key]: null });
          setStep(step + 1);
        }
      }
      return;
    }
    const value = input || '';
    if (field.required && !value.trim()) {
      setError('This field is required.');
      return;
    }
    if (field.validate && value && !field.validate(value)) {
      setError('Invalid input format.');
      return;
    }
    setAnswers({ ...answers, [field.key]: value });
    setInput('');
    setError('');
    if (step < initialFields.length - 1) {
      setStep(step + 1);
    } else {
      setSubmitted(true);
    }
  };

  const handleEdit = key => {
    setStep(initialFields.findIndex(f => f.key === key));
    setSubmitted(false);
    if (initialFields.find(f => f.key === key).type === 'file') {
      setFileInput(answers[key] || null);
      setInput('');
    } else {
      setInput(answers[key] || '');
      setFileInput(null);
    }
  };

  if (closed) return null;
  if (minimized) {
    return (
      <div style={{ position: 'fixed', bottom: 30, right: 30, zIndex: 9999 }}>
        <button onClick={() => setMinimized(false)} style={{ background: 'linear-gradient(90deg,#0d5097 60%,#0080ff 100%)', color: '#fff', border: 'none', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.12)', padding: '0.7em 1.2em', fontSize: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
          <FaRobot style={{ fontSize: 22 }} /> <span style={{ fontWeight: 600 }}>Open BlistBot</span>
        </button>
      </div>
    );
  }

  return (
    <div className="business-bot-chatbox" style={{  position: 'fixed', bottom: 30, right: 30, zIndex: 9999, borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.12)', background: '#fff', overflow: 'hidden', border: '1px solid #e0e0e0' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(90deg,#0d5097 60%,#0080ff 100%)', color: '#fff', padding: '1em', display: 'flex', alignItems: 'center', borderTopLeftRadius: 16, borderTopRightRadius: 16, justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <FaRobot style={{ fontSize: 28, marginRight: 10 }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 18 }}>Add New Business with Ai </div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>We are online!</div>
          </div>
        </div>
        <div>
          <button onClick={() => setMinimized(true)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', marginLeft: 6 }} aria-label="Minimize bot"><FaMinus /></button>
          <button onClick={() => setClosed(true)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', marginLeft: 6 }} aria-label="Close bot"><FaTimes /></button>
        </div>
      </div>
      {/* Chat bubbles */}
      <div style={{ padding: '1em', background: '#f7f9fc', minHeight: 250, maxHeight: 320, overflowY: 'auto' }}>
        {chatHistory.map((msg, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: msg.from === 'bot' ? 'flex-start' : 'flex-end', marginBottom: 8 }}>
            <div style={{ background: msg.from === 'bot' ? '#e3f0ff' : '#fff', color: msg.from === 'bot' ? '#0d5097' : '#222', borderRadius: 12, padding: '8px 14px', maxWidth: '80%', fontSize: 15, border: msg.from === 'user' ? '1px solid #e0e0e0' : 'none' }}>
              {msg.text}
            </div>
          </div>
        ))}
        {error && <div style={{ color: 'red', marginBottom: '1em', textAlign: 'center' }}>{error}</div>}
      </div>
      {/* Input box */}
      {!submitted && (
        <div style={{ padding: '0.5em 1em', background: '#fff', borderBottomLeftRadius: 16, borderBottomRightRadius: 16, borderTop: '1px solid #e0e0e0', display: 'flex', alignItems: 'center' }}>
          {field.type === 'textarea' ? (
            <textarea value={input} onChange={e => setInput(e.target.value)} rows={2} style={{ width: '100%', marginRight: 8, borderRadius: 8, border: '1px solid #e0e0e0', padding: '8px' }} />
          ) : field.type === 'file' ? (
            <input type="file" accept="image/*" onChange={e => setFileInput(e.target.files[0])} style={{ width: '100%', marginRight: 8, borderRadius: 8, border: '1px solid #e0e0e0', padding: '8px' }} />
          ) : (
            <input type={field.type} value={input} onChange={e => setInput(e.target.value)} style={{ width: '100%', marginRight: 8, borderRadius: 8, border: '1px solid #e0e0e0', padding: '8px' }} />
          )}
          <button style={{ background: '#0d5097', color: '#fff', border: 'none', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }} onClick={handleSend}><FaPaperPlane /></button>
        </div>
      )}
    </div>
  );
}

export default BusinessListingBot;
